(function () {
  'use strict';

  // Replaces Shopify's native /contact submission with a call to a custom
  // relay (Vercel + Resend), so only ONE branded notification email is sent
  // per enquiry (clickable phone, WhatsApp link, Enquiry Source, Page URL).
  //
  // Fallback: if the relay itself fails (network error, 5xx), the form is
  // allowed to submit to Shopify natively exactly once, so a lead is never
  // silently lost. The one exception is HTTP 422, which the relay uses to say
  // "rejected as spam / failed verification": no fallback, the visitor sees an
  // error and can retry.
  //
  // Anti-spam layers (added 28 Sep), applied to every /contact form on the site:
  //   1. Honeypot: a hidden field humans never see. If it is filled we fake a
  //      success and send nothing.
  //   2. Light client-side validation (phone, budget, name, message).
  //   3. Cloudflare Turnstile. Stays OFF until TURNSTILE_SITE_KEY is set below.
  //      The token is sent as turnstileToken and MUST be verified by the relay
  //      (server side). Browser checks alone cannot stop bots that post
  //      straight to RELAY_URL.
  //
  // Duplicate-send hardening: a form can only ever be relayed once, enforced by
  // a permanent per-form flag, a client-generated idempotency key (forwarded
  // to Resend), and a success UI that can never throw into the failure path.

  var RELAY_URL = 'https://bigsmall-lead-relay.vercel.app/api/lead';

  // Public Cloudflare Turnstile site key. Leave blank to keep Turnstile off.
  var TURNSTILE_SITE_KEY = '';
  var TURNSTILE_WAIT_MS = 6000;
  var HONEYPOT_NAME = 'bs_hp_website';

  var submittedForms = new WeakSet();
  var turnstileWidgets = new WeakMap();

  function isContactForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return /\/contact(\b|#|\?|$)/i.test(action);
  }

  function extractFields(form) {
    var fields = {};
    try {
      var data = new FormData(form);
      data.forEach(function (value, key) {
        var match = key.match(/^contact\[(.+)\]$/);
        if (!match) return;
        if (typeof value !== 'string') return;
        if (value.trim() === '') return;
        fields[match[1]] = value;
      });
    } catch (e) {
      // ignore -- if FormData construction fails for any reason, treat as empty
    }
    return fields;
  }

  function makeIdempotencyKey() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    return 'bs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function injectStyles() {
    if (document.getElementById('bs-relay-success-style')) return;
    var style = document.createElement('style');
    style.id = 'bs-relay-success-style';
    style.textContent =
      '.bs-relay-success{padding:16px 18px;margin:0 0 16px;border:1px solid #b9dfca;' +
      'border-radius:10px;background:#f1fbf5;color:#165c35;' +
      'font:600 14px/1.5 Lato,Arial,sans-serif;}' +
      '.bs-relay-error{padding:14px 18px;margin:0 0 16px;border:1px solid #efc2c2;' +
      'border-radius:10px;background:#fdf3f3;color:#8a1f1f;' +
      'font:600 14px/1.5 Lato,Arial,sans-serif;}' +
      '.bs-turnstile{margin:0;}';
    document.head.appendChild(style);
  }

  function clearError(form) {
    var prev = form.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('bs-relay-error')) prev.remove();
  }

  function showError(form, text) {
    try {
      injectStyles();
      clearError(form);
      var message = document.createElement('div');
      message.className = 'bs-relay-error';
      message.setAttribute('role', 'alert');
      message.textContent = text;
      form.insertAdjacentElement('beforebegin', message);
    } catch (e) {}
  }

  function showSuccess(form) {
    // Deliberately isolated in its own try/catch: a DOM error here must
    // never fall through to the caller's .catch() and trigger fallbackSubmit(),
    // which would send a second (native Shopify) email.
    try {
      injectStyles();
      var message = document.createElement('div');
      message.className = 'bs-relay-success';
      message.setAttribute('role', 'status');
      message.tabIndex = -1;
      message.textContent = 'Thank you. Your enquiry has been received \u2014 our team will get in touch with you shortly.';
      form.hidden = true;
      form.insertAdjacentElement('beforebegin', message);
      message.focus();
    } catch (e) {}
  }

  function fallbackSubmit(form) {
    // Native Shopify submission, used only when the relay itself failed.
    // form.submit() does not re-dispatch the 'submit' event, so this can
    // never loop back into this same handler.
    try {
      form.submit();
    } catch (e) {}
  }

  function resetSubmitButton(form) {
    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (btn && btn.dataset.bsOriginalLabel !== undefined) {
      btn.innerHTML = btn.dataset.bsOriginalLabel;
      btn.disabled = false;
    }
  }

  // ---- 1. Honeypot -------------------------------------------------------

  function addHoneypot(form) {
    try {
      var wrap = document.createElement('div');
      wrap.setAttribute('aria-hidden', 'true');
      wrap.style.cssText = 'position:absolute!important;left:-10000px!important;top:auto!important;width:1px!important;height:1px!important;overflow:hidden!important;';
      var input = document.createElement('input');
      input.type = 'text';
      input.name = HONEYPOT_NAME;
      input.tabIndex = -1;
      input.setAttribute('autocomplete', 'off');
      input.value = '';
      wrap.appendChild(input);
      form.appendChild(wrap);
    } catch (e) {}
  }

  function honeypotTripped(form) {
    var input = form.querySelector('input[name="' + HONEYPOT_NAME + '"]');
    return !!(input && input.value.trim() !== '');
  }

  // ---- 2. Client-side validation ----------------------------------------
  // Deliberately conservative: only known field names are checked, and each
  // rule is one a real enquiry will always pass. Stricter scoring belongs on
  // the relay, where flagged leads can be quarantined instead of blocked.

  function countLetters(value) {
    var m = value.match(/\p{L}/gu);
    return m ? m.length : 0;
  }

  function validate(fields) {
    var keys = Object.keys(fields);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      var k = key.toLowerCase();
      var v = String(fields[key]).trim();

      if (k === 'phone') {
        var digits = v.replace(/\D/g, '');
        // The +91 prefix is pre-filled by the inquiry guard, so treat it alone as empty.
        if (digits === '' || digits === '91') continue;
        if (digits.length === 12 && digits.indexOf('91') === 0) digits = digits.slice(2);
        else if (digits.length === 11 && digits.charAt(0) === '0') digits = digits.slice(1);
        if (!/^[6-9]\d{9}$/.test(digits)) {
          return { key: key, message: 'Please enter a valid 10-digit mobile number.' };
        }
      } else if (k === 'budget') {
        if (!/\d/.test(v) || v.length > 40) {
          return { key: key, message: 'Please enter your budget in numbers, for example 1500 or 1000-2000.' };
        }
      } else if (k === 'body' || k === 'message') {
        if (countLetters(v) < 2) {
          return { key: key, message: 'Please describe your requirement in a few words.' };
        }
        if ((v.match(/https?:|www\./gi) || []).length > 2) {
          return { key: key, message: 'Please remove links from your message.' };
        }
      } else if (k === 'name') {
        if (countLetters(v) < 2 || v.length > 80 || /https?:|www\./i.test(v)) {
          return { key: key, message: 'Please enter your full name.' };
        }
      }
    }
    return null;
  }

  function showProblem(form, problem) {
    var input = form.querySelector('[name="contact[' + problem.key + ']"]');
    if (!input || typeof input.setCustomValidity !== 'function') {
      showError(form, problem.message);
      return;
    }
    input.setCustomValidity(problem.message);
    input.addEventListener('input', function () { input.setCustomValidity(''); }, { once: true });
    input.reportValidity();
  }

  // ---- 3. Cloudflare Turnstile ------------------------------------------

  function loadTurnstile(cb) {
    if (window.turnstile) { cb(); return; }
    if (loadTurnstile.queue) { loadTurnstile.queue.push(cb); return; }
    loadTurnstile.queue = [cb];
    var s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = function () {
      var q = loadTurnstile.queue || [];
      loadTurnstile.queue = null;
      q.forEach(function (fn) { try { fn(); } catch (e) {} });
    };
    s.onerror = function () { loadTurnstile.queue = null; };
    document.head.appendChild(s);
  }

  function renderWidget(form) {
    if (!window.turnstile || turnstileWidgets.has(form)) return;
    try {
      var host = document.createElement('div');
      host.className = 'bs-turnstile';
      var btn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (btn && btn.parentNode) btn.parentNode.insertBefore(host, btn);
      else form.appendChild(host);
      var id = window.turnstile.render(host, {
        sitekey: TURNSTILE_SITE_KEY,
        appearance: 'interaction-only',
        'refresh-expired': 'auto'
      });
      turnstileWidgets.set(form, id);
    } catch (e) {}
  }

  function getToken(form) {
    var id = turnstileWidgets.get(form);
    if (id === undefined || !window.turnstile) return '';
    try { return window.turnstile.getResponse(id) || ''; } catch (e) { return ''; }
  }

  function resetTurnstile(form) {
    var id = turnstileWidgets.get(form);
    if (id === undefined || !window.turnstile) return;
    try { window.turnstile.reset(id); } catch (e) {}
  }

  function waitForToken(form, done) {
    var started = Date.now();
    (function poll() {
      var token = getToken(form);
      if (token) { done(token); return; }
      if (Date.now() - started >= TURNSTILE_WAIT_MS) { done(''); return; }
      setTimeout(poll, 200);
    })();
  }

  // ---- Wiring ------------------------------------------------------------

  function enhanceForm(form) {
    if (!isContactForm(form) || form.dataset.bsGuarded === '1') return;
    form.dataset.bsGuarded = '1';
    addHoneypot(form);
    if (TURNSTILE_SITE_KEY) loadTurnstile(function () { renderWidget(form); });
  }

  function enhanceAll(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('form[action*="/contact"]').forEach(enhanceForm);
  }

  function rejectSubmission(form) {
    // Isolated in try/catch so it can never fall into the .catch() fallback.
    try {
      resetSubmitButton(form);
      submittedForms.delete(form);
      resetTurnstile(form);
      showError(form, 'We could not verify this submission. Please refresh the page and try again, or call us directly.');
    } catch (e) {}
  }

  function sendToRelay(form, payload) {
    fetch(RELAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (res.status === 422) {
          rejectSubmission(form);
          return;
        }
        if (!res.ok) throw new Error('relay failed');
        showSuccess(form);
      })
      .catch(function () {
        resetSubmitButton(form);
        fallbackSubmit(form);
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isContactForm(form)) return;

    // Hard stop: once a form has been accepted for submission once, every
    // subsequent 'submit' event on that same element is ignored outright.
    if (submittedForms.has(form)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    clearError(form);

    var fields = extractFields(form);
    if (!Object.keys(fields).length) {
      submittedForms.add(form);
      fallbackSubmit(form);
      return;
    }

    // Bot filled the hidden field: pretend it worked, send nothing.
    if (honeypotTripped(form)) {
      submittedForms.add(form);
      showSuccess(form);
      return;
    }

    // Validation failures do NOT mark the form as submitted, so the visitor
    // can fix the field and try again.
    var problem = validate(fields);
    if (problem) {
      showProblem(form, problem);
      return;
    }

    submittedForms.add(form);

    var btn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (btn) {
      btn.dataset.bsOriginalLabel = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = 'Sending\u2026';
    }

    var payload = {
      idempotencyKey: makeIdempotencyKey(),
      enquirySource:
        fields['Enquiry Source'] ||
        fields['Enquiry type'] ||
        fields['Enquiry source'] ||
        document.title,
      pageUrl: fields['Page URL'] || fields['page_url'] || window.location.href,
      fields: fields
    };

    if (!window.fetch) {
      fallbackSubmit(form);
      return;
    }

    if (!TURNSTILE_SITE_KEY) {
      sendToRelay(form, payload);
      return;
    }

    // Turnstile is on: wait briefly for the token. If Cloudflare could not
    // load (ad blocker, network), fall back to Shopify's own form so a real
    // enquiry still gets through.
    waitForToken(form, function (token) {
      if (!token) {
        resetSubmitButton(form);
        fallbackSubmit(form);
        return;
      }
      payload.turnstileToken = token;
      sendToRelay(form, payload);
    });
  }, true);

  function init() {
    enhanceAll(document);
    document.addEventListener('shopify:section:load', function (event) {
      enhanceAll(event.target);
    });
    if (window.MutationObserver) {
      var timer = null;
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(function () { enhanceAll(document); }, 250);
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
